import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  useHideWordMutation,
  useRestoreWordMutation,
  WordBrowser,
  WordPreview,
  type WordDotTone,
  type WordPreviewContext,
} from '@entities/Word';
import { buildComposerState, paths } from '@shared/config';
import type { ModuleSummary, Word } from '@shared/types';
import { toast } from '@shared/ui';
import { useModuleWords, type WordsFilter } from '../lib/useModuleWords';
import './ModuleDetail.css';

interface ModuleWordsProps {
  module: ModuleSummary;
  /** ≤900px: карточка слова открывается нижним листом. */
  mobile: boolean;
}

type Status = Exclude<WordDotTone, 'none'>;

/**
 * Статус кодирует «здоровье/происхождение» карточки, а НЕ FSRS-этап:
 * красный — требует действия, серый — создано вручную, зелёный — разобрано.
 */
const statusOf = (w: Word): Status => (w.isNeedAction ? 'danger' : w.isManual ? 'manual' : 'ready');

function SearchIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
      <circle cx="11" cy="11" r="7" />
      <path d="M20 20l-3.5-3.5" />
    </svg>
  );
}

/** Слова модуля: поиск, «Все / Удалённые» и карточка выбранного слова с действием. */
export function ModuleWords({ module: m, mobile }: ModuleWordsProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isLoading, filter, setFilter, query, setQuery, counts, filtered } = useModuleWords(m.id);
  const hide = useHideWordMutation();
  const restore = useRestoreWordMutation();

  const statusLabel: Record<Status, string> = {
    danger: t('moduleDetail.words.chips.needAction'),
    manual: t('moduleDetail.words.dot.manual'),
    ready: t('moduleDetail.words.dot.ready'),
  };
  const posLabel = (pos: string) => t(`moduleDetail.words.pos.${pos}`, pos);

  const handleHide = (w: Word) => {
    if (!w.id || !w.meaningId) return;
    hide.mutate(
      { unitId: m.id, cardId: w.id, meaningId: w.meaningId },
      {
        onSuccess: () => toast({ tone: 'info', message: t('moduleDetail.words.hidden', { word: w.word }) }),
        onError: () => toast({ tone: 'error', message: t('moduleDetail.words.actionFailed') }),
      },
    );
  };

  const handleRestore = (w: Word) => {
    if (!w.id || !w.meaningId) return;
    restore.mutate(
      { unitId: m.id, cardId: w.id, meaningId: w.meaningId },
      {
        onSuccess: () => toast({ tone: 'success', message: t('moduleDetail.words.restored', { word: w.word }) }),
        onError: () => toast({ tone: 'error', message: t('moduleDetail.words.actionFailed') }),
      },
    );
  };

  // Скрыть/восстановить можно только real-state (есть meaningId). Карточку,
  // которую алгоритм не разобрал, без части речи отправляем разбирать в карточки.
  const cardAction = (w: Word, { closeSheet }: WordPreviewContext) => {
    if (w.meaningId && w.isHidden) {
      return (
        <button
          type="button"
          className="md-card-act"
          data-tone="restore"
          disabled={restore.isPending}
          onClick={() => {
            closeSheet();
            handleRestore(w);
          }}
        >
          {t('moduleDetail.words.restore')}
        </button>
      );
    }
    if (w.meaningId) {
      return (
        <button
          type="button"
          className="md-card-act"
          disabled={hide.isPending}
          onClick={() => {
            closeSheet();
            handleHide(w);
          }}
        >
          {t('moduleDetail.words.hide')}
        </button>
      );
    }
    if (w.isNeedAction) {
      return (
        <button
          type="button"
          className="md-card-act"
          data-tone="resolve"
          // Страницы «Из словаря» больше нет (2026-10-21): слово и модуль — в строку создания на «Словах и модулях».
          onClick={() => navigate(paths.words, { state: buildComposerState(m.id, w.word) })}
        >
          {t('moduleDetail.words.resolve')}
        </button>
      );
    }
    return null;
  };

  const tabs: { key: WordsFilter; label: string; count: number }[] = [
    { key: 'all', label: t('moduleDetail.words.filters.all'), count: counts.all },
    { key: 'hidden', label: t('moduleDetail.words.filters.hidden'), count: counts.hidden },
  ];

  const header = (
    <>
      <div className="md-words-head">
        <h2 className="md-words-title">{t('moduleDetail.words.title', { n: counts.all, count: counts.all })}</h2>
        <button
          type="button"
          className="md-create"
          aria-label={mobile ? t('moduleDetail.words.createCard') : undefined}
          onClick={() => navigate(paths.words, { state: buildComposerState(m.id) })}
        >
          <span className="md-create-plus" aria-hidden="true">+</span>
          {t(mobile ? 'moduleDetail.words.createShort' : 'moduleDetail.words.createCard')}
        </button>
      </div>
      <div className="md-toolbar">
        <label className="md-search">
          <SearchIcon />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('moduleDetail.words.search')}
            aria-label={t('moduleDetail.words.search')}
          />
        </label>
        <div className="md-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className="md-tab"
              aria-pressed={filter === tab.key}
              onClick={() => setFilter(tab.key)}
            >
              {tab.label}
              <span className="md-tab-count">{tab.count}</span>
            </button>
          ))}
        </div>
      </div>
    </>
  );

  return (
    <div className="md-words">
      <WordBrowser
        words={filtered}
        mobile={mobile}
        header={header}
        empty={
          isLoading ? (
            <div className="md-skel" aria-hidden="true">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="animate-skeleton" />
              ))}
            </div>
          ) : (
            <div className="md-empty">{t('moduleDetail.words.empty')}</div>
          )
        }
        rowMeta={(w) => {
          const status = statusOf(w);
          return {
            sub: status === 'danger' ? statusLabel.danger : undefined,
            subTone: status === 'danger' ? 'danger' : undefined,
            // Точка только у проблемных: создано вручную и требует действия.
            dot: status === 'ready' ? 'none' : status,
            dotLabel: statusLabel[status],
          };
        }}
        renderPreview={(w, ctx) => {
          const status = statusOf(w);
          return (
            <WordPreview
              word={w}
              posLabel={posLabel}
              alert={w.isNeedAction ? t('moduleDetail.words.needActionText') : undefined}
              // Последняя карточка слова убирает его так же, как «Скрыть».
              onDeleteWord={
                w.meaningId && !w.isHidden
                  ? () => {
                      ctx.closeSheet();
                      handleHide(w);
                    }
                  : undefined
              }
              footer={
                <>
                  <span className="md-status">
                    <span className="md-status-dot" data-tone={status} aria-hidden="true" />
                    {statusLabel[status]}
                  </span>
                  {cardAction(w, ctx)}
                </>
              }
            />
          );
        }}
      />
    </div>
  );
}
